import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import BackLink from '../components/BackLink';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import LogoSpinner from '../components/ui/LogoSpinner';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import ForecastChart from '../Dashboard/ForecastChart';
import { ITEM_TYPES, variantOfItemType, productOfItemType, lineLabel } from './itemTypes';
import useUserStore from '../stores/useUserStore';
import { maySeeSalesPrices, formatMoney, lineAmount, orderTotal, totalCaveat, pricePlaceholder } from './salesPrices';
import { useBomsApi } from '../Parts/bomsApi';
import { productOptionsFrom, isStockItem, itemChoices } from './salesApi';
import { variantLabel } from '../Parts/bomsApi';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass, IconButton } from '../components/ui/Button';
import { Field, FieldRow, TextArea } from '../components/ui/Field';
import Select from '../components/ui/Select';
import { Tooltip } from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import type { ProductOption, ItemChoice } from './salesApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SALE_STATUSES = ['pending', 'confirmed', 'in_production', 'ready', 'shipped', 'cancelled'];
const LEASE_RENTAL_STATUSES = ['pending', 'confirmed', 'shipping', 'delivered', 'awaiting_return', 'returned', 'cancelled'];

// ---------------------------------------------------------------------------
// CustomerPicker — autocomplete input with dropdown
// ---------------------------------------------------------------------------

interface PickerCustomer {
  id: number;
  name: string;
  contact_name?: string;
}

interface CustomerPickerProps {
  customers: PickerCustomer[];
  selectedId: string | number;
  freetext: string;
  onSelect: (id: string | number, name: string) => void;
  onNewCustomer: (isNew: boolean) => void;
}

const CustomerPicker: React.FC<CustomerPickerProps> = ({ customers, selectedId, freetext, onSelect, onNewCustomer }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Sync display text when external state changes (e.g. edit mode load)
  useEffect(() => {
    if (selectedId) {
      const c = customers.find(c => String(c.id) === String(selectedId));
      if (c) setQuery(c.name);
    } else if (freetext) {
      setQuery(freetext);
    }
  }, [selectedId, freetext, customers]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? customers.filter(c =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        (c.contact_name && c.contact_name.toLowerCase().includes(query.toLowerCase()))
      )
    : customers;

  const exactMatch = customers.some(c => c.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <div ref={ref} className="relative">
      <Field
        type="text"
        size="lg"
        value={query}
        placeholder="Start typing a customer name..."
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
          onNewCustomer(false);
          if (selectedId) onSelect('', e.target.value);
          else onSelect('', e.target.value);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && query.trim().length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-surface-container-high rounded-md shadow-overlay max-h-56 overflow-y-auto">
          {filtered.map(c => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-surface-container-highest transition-colors flex items-center justify-between"
              onClick={() => {
                onSelect(c.id, c.name);
                onNewCustomer(false);
                setQuery(c.name);
                setOpen(false);
              }}
            >
              <span className="text-white text-sm">{c.name}</span>
              {c.contact_name && <span className="text-on-surface-variant text-xs ml-2">{c.contact_name}</span>}
            </button>
          ))}
          {!exactMatch && query.trim() && (
            <button
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-surface-container-highest transition-colors border-t border-outline-variant"
              onClick={() => {
                onSelect('', query.trim());
                onNewCustomer(true);
                setOpen(false);
              }}
            >
              <span className="text-primary text-sm font-medium">+ Add &ldquo;{query.trim()}&rdquo; as new customer</span>
            </button>
          )}
          {filtered.length === 0 && exactMatch && (
            <div className="px-4 py-3 text-on-surface-variant text-sm">No other matches</div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The two line tables, as shared-DataTable columns
// ---------------------------------------------------------------------------

/**
 * Module scope per the DataTable contract: nothing here closes over page state. What changes per
 * render (the product names, the remove handlers) rides `meta`. Prices are free text until ERP
 * chunk 4, so a line can say "TBD": the total's caveat names what was excluded rather than
 * printing a confident number for the rest. Two arrays per table, because a footer cannot be
 * added or dropped per render.
 */
interface LineMeta {
  productLabel: (productId: number | null) => string | null;
  onRemove: (index: number) => void;
  onDelete: (lineId: string) => void;
}

interface NewLineRow {
  item_type: string;
  quantity: number;
  unit_price_text?: string | null;
  part_catalog_id: number | null;
  variant: string | null;
}

interface SavedLineRow {
  id: string;
  item_type: string;
  quantity: number;
  unit_price_text?: string | null;
  rental_duration_days?: number | null;
  notes?: string | null;
  product_name?: string | null;
  variant?: string | null;
}

const metaOf = (info: { table: { options: { meta?: object } } }) => info.table.options.meta as LineMeta;

const rowModelTotal = (ctx: { table: { getRowModel: () => { rows: { original: any }[] } } }) =>
  orderTotal(ctx.table.getRowModel().rows.map((r) => r.original));

const amountCell = (line: { item_type: string; quantity: number; unit_price_text?: string | null }) => {
  const a = lineAmount(line);
  return a == null ? '\u2014' : formatMoney(a);
};

const QTY: ColumnDef<any, any> = {
  id: 'quantity', header: 'Qty', meta: { align: 'right', numeric: true, cellClass: 'text-white font-medium' },
  cell: ({ row }) => row.original.quantity,
};
const RATE: ColumnDef<any, any> = {
  id: 'rate', header: 'Rate', meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
  cell: ({ row }) => row.original.unit_price_text || '\u2014',
};
const AMOUNT: ColumnDef<any, any> = {
  id: 'amount', header: 'Amount', meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
  cell: ({ row }) => amountCell(row.original),
};
const RATE_TOTALED: ColumnDef<any, any> = {
  ...RATE, footer: () => <span className="font-normal text-on-surface-variant">Total</span>,
};
const AMOUNT_TOTALED: ColumnDef<any, any> = {
  ...AMOUNT, footer: (ctx) => formatMoney(rowModelTotal(ctx).total),
};

const NEW_ITEM: ColumnDef<NewLineRow, any> = {
  id: 'item', header: 'Item', meta: { cellClass: 'text-white' },
  cell: (info) => (
    <LineItemLabel itemType={info.row.original.item_type} productName={metaOf(info).productLabel(info.row.original.part_catalog_id)} variant={info.row.original.variant} />
  ),
};
const NEW_REMOVE: ColumnDef<NewLineRow, any> = {
  id: 'remove', header: () => <span className="sr-only">Remove</span>, meta: { align: 'right' },
  cell: (info) => (
    <Tooltip label="Remove line">
      <IconButton label="Remove line" size="sm" onClick={() => metaOf(info).onRemove(info.row.index)}>
        <IconClose className="h-3.5 w-3.5" />
      </IconButton>
    </Tooltip>
  ),
};
const NEW_COLUMNS: ColumnDef<NewLineRow, any>[] = [NEW_ITEM, QTY, RATE, AMOUNT, NEW_REMOVE];
const NEW_COLUMNS_TOTALED: ColumnDef<NewLineRow, any>[] = [NEW_ITEM, QTY, RATE_TOTALED, AMOUNT_TOTALED, NEW_REMOVE];

const SAVED_ITEM: ColumnDef<SavedLineRow, any> = {
  id: 'item', header: 'Item', meta: { cellClass: 'text-white' },
  cell: ({ row }) => <LineItemLabel itemType={row.original.item_type} productName={row.original.product_name} variant={row.original.variant} />,
};
const DAYS: ColumnDef<SavedLineRow, any> = {
  id: 'days', header: 'Days', meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
  cell: ({ row }) => row.original.rental_duration_days || '\u2014',
};
const SAVED_NOTES: ColumnDef<SavedLineRow, any> = {
  id: 'notes', header: 'Notes', meta: { cellClass: 'text-on-surface-variant' },
  cell: ({ row }) => row.original.notes || '\u2014',
};
const SAVED_DELETE: ColumnDef<SavedLineRow, any> = {
  id: 'delete', header: () => <span className="sr-only">Delete</span>, meta: { align: 'right' },
  cell: (info) => (
    <Tooltip label="Delete line">
      <IconButton label="Delete line" size="sm" className="!text-red-300 hover:!text-red-200" onClick={() => metaOf(info).onDelete(info.row.original.id)}>
        <IconClose className="h-3.5 w-3.5" />
      </IconButton>
    </Tooltip>
  ),
};
const SAVED_COLUMNS: ColumnDef<SavedLineRow, any>[] = [SAVED_ITEM, QTY, DAYS, RATE, AMOUNT, SAVED_NOTES, SAVED_DELETE];
const SAVED_COLUMNS_TOTALED: ColumnDef<SavedLineRow, any>[] = [SAVED_ITEM, QTY, DAYS, RATE_TOTALED, AMOUNT_TOTALED, SAVED_NOTES, SAVED_DELETE];

/** Frozen, one per combination: a fresh object per render would invalidate tanstack's cell memo. */
const NEW_VIS = {
  true: { rate: true, amount: true },
  false: { rate: false, amount: false },
};
const SAVED_VIS = {
  'true-true': { rate: true, amount: true, days: true },
  'true-false': { rate: true, amount: true, days: false },
  'false-true': { rate: false, amount: false, days: true },
  'false-false': { rate: false, amount: false, days: false },
} as const;

// ---------------------------------------------------------------------------
// Line product: the model a stock line sells, picked and shown
// ---------------------------------------------------------------------------

interface LineProductFieldsProps {
  products: ProductOption[];
  productId: string;
  variant: string;
  /** A drone item type decides the build itself, so the Variant field stays out of its way. */
  itemTypeFixesVariant: boolean;
  /** False when the Item choice already named the model: only its Variant is left to pick. */
  showProduct: boolean;
  onChange: (productId: string, variant: string) => void;
}

/** Product, then Variant once the chosen model's parts list has any. Both optional. */
const LineProductFields: React.FC<LineProductFieldsProps> = ({ products, productId, variant, itemTypeFixesVariant, showProduct, onChange }) => {
  const chosen = products.find(p => String(p.id) === productId);
  return (
    <>
      {showProduct && (
        <FieldRow label="Product" htmlFor="line-product">
          <Select
            label="Product"
            value={productId}
            onChange={key => onChange(key, '')}
            options={[{ key: '', label: 'No product' }, ...products.map(p => ({ key: String(p.id), label: p.label }))]}
          />
        </FieldRow>
      )}
      {chosen && chosen.variants.length > 0 && !itemTypeFixesVariant && (
        <FieldRow label="Variant" htmlFor="line-variant">
          <Select
            label="Variant"
            value={variant}
            onChange={key => onChange(productId, key)}
            options={[{ key: '', label: 'Choose a build' }, ...chosen.variants.map(v => ({ key: v, label: variantLabel(v) }))]}
          />
        </FieldRow>
      )}
    </>
  );
};

/** The item, and beside it the product the line sells, or a quiet hint on a stock line that could name one. */
const LineItemLabel: React.FC<{ itemType: string; productName?: string | null; variant?: string | null }> = ({ itemType, productName, variant }) => {
  const namedByModel = productOfItemType(itemType) === 'model';
  return (
    <>
      {lineLabel(itemType, productName)}
      {productName && !namedByModel ? (
        <span className="ml-2 text-on-surface-variant">
          {productName}{variant ? `, ${variantLabel(variant)}` : null}
        </span>
      ) : namedByModel && variant ? (
        <span className="ml-2 text-on-surface-variant">{variantLabel(variant)}</span>
      ) : isStockItem(itemType) && productOfItemType(itemType) === null ? (
        <span className="ml-2 text-xs text-on-surface-disabled">No product</span>
      ) : null}
    </>
  );
};

/** The Item dropdown. A choice may carry its model, so its value is the choice key, not the item type. */
const ItemChoiceSelect: React.FC<{ choices: ItemChoice[]; itemType: string; productId: string; onChange: (choice: ItemChoice) => void }> = ({ choices, itemType, productId, onChange }) => {
  const value = productOfItemType(itemType) === 'model' && productId ? `${itemType}:${productId}` : itemType;
  return (
    <FieldRow label="Item Type" htmlFor="line-item">
      <Select
        label="Item type"
        value={value || null}
        placeholder="Select an item"
        onChange={key => { const c = choices.find(x => x.key === key); if (c) onChange(c); }}
        options={choices.map(c => ({ key: c.key, label: c.label }))}
      />
    </FieldRow>
  );
};

// ---------------------------------------------------------------------------
// SalesOrderForm
// ---------------------------------------------------------------------------

const SalesOrderForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const api = useApi();
  const toast = useToast();
  const isEdit = !!id;

  // Read type and customer from URL query params
  const searchParams = new URLSearchParams(window.location.search);
  const defaultType = searchParams.get('type') || 'sale';
  const defaultCustomerId = searchParams.get('customer') || '';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState<PickerCustomer[]>([]);
  const [order, setOrder] = useState<any>(null);
  const [lines, setLines] = useState<any[]>([]);
  const [availability, setAvailability] = useState<any>(null);
  const [newLines, setNewLines] = useState<{ item_type: string; quantity: number; unit_price_text?: string | null; part_catalog_id: number | null; variant: string | null }[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const bomsApi = useBomsApi();

  const [form, setForm] = useState({
    customer_org_id: defaultCustomerId as string | number,
    customer_name: '',
    order_type: defaultType,
    status: 'pending',
    delivery_date: '',
    effective_date: '',
    end_date: '',
    ship_date: '',
    notes: '',
  });

  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ contact_name: '', contact_email: '', contact_phone: '', notes: '' });

  const [lineForm, setLineForm] = useState({
    item_type: '',
    quantity: '',
    rental_duration_days: '',
    notes: '',
    unit_price_text: '',
    part_catalog_id: '',
    variant: '',
  });

  // The API strips prices from the response for anyone who may not see them, so
  // this only decides whether to render an input they could not save. One place,
  // mirrored in api/src/sales/sales-price-visibility.js.
  const user = useUserStore(st => st.user);
  const mayPrice = maySeeSalesPrices(user);

  // Fetch customers + availability on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const data = await api.get('/orders/customers');
        setCustomers(data || []);
      } catch { /* silent */ }
    };
    const fetchAvailability = async () => {
      try {
        const data = await api.get('/dashboard');
        setAvailability(data.fleetCounts);
      } catch { /* silent */ }
    };
    const fetchProducts = async () => {
      try {
        setProducts(productOptionsFrom(await bomsApi.listProducts()));
      } catch {
        // Silent where a model is optional; where an item IS a model, its choices are gone, so say so.
        if (ITEM_TYPES.some(t => t.product === 'model')) toast.error('Could not load the product list. Reload to sell a drone.');
      }
    };
    fetchCustomers();
    fetchAvailability();
    fetchProducts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch order on edit
  useEffect(() => {
    if (!isEdit) return;
    const fetchOrder = async () => {
      try {
        const data = await api.get(`/orders/${id}`);
        setOrder(data);
        setLines(data.lines || []);
        setForm({
          customer_org_id: data.customer_org_id || '',
          customer_name: data.customer_name || '',
          order_type: data.order_type || 'sale',
          status: data.status || 'pending',
          delivery_date: data.delivery_date ? data.delivery_date.split('T')[0] : '',
          effective_date: data.effective_date ? data.effective_date.split('T')[0] : '',
          end_date: data.end_date ? data.end_date.split('T')[0] : '',
          ship_date: data.ship_date ? data.ship_date.split('T')[0] : '',
          notes: data.notes || '',
        });
      } catch {
        toast.error('Order not found');
        history.push('/orders');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: string, value: string | number) => setForm(f => ({ ...f, [key]: value }));

  // A model choice names its product; a type that sells none clears it.
  const setLineChoice = (choice: ItemChoice) => setLineForm(f => ({
    ...f,
    item_type: choice.itemType,
    ...(choice.productId !== null
      ? { part_catalog_id: String(choice.productId), variant: '' }
      : isStockItem(choice.itemType) && productOfItemType(choice.itemType) === null ? {} : { part_catalog_id: '', variant: '' }),
  }));
  const choices = itemChoices(ITEM_TYPES, products);
  const setLineProduct = (productId: string, variant: string) =>
    setLineForm(f => ({ ...f, part_catalog_id: productId, variant }));
  const lineProductBody = () => ({
    part_catalog_id: lineForm.part_catalog_id ? parseInt(lineForm.part_catalog_id) : null,
    variant: lineForm.part_catalog_id
      ? (variantOfItemType(lineForm.item_type) ?? (lineForm.variant || null))
      : null,
  });
  const lineProduct = productOfItemType(lineForm.item_type);
  // A drone line (one that fixes a build) sells a model; any other stock line may sell any product with a list.
  const lineProducts = variantOfItemType(lineForm.item_type) !== null ? products.filter((p) => p.is_model) : products;
  const showProductFields = isStockItem(lineForm.item_type) && products.length > 0 && lineProduct !== 'none';
  const productLabel = (productId: number | null) => products.find(p => p.id === productId)?.label || null;

  // ---------- Submit ----------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customer_org_id && !form.customer_name) {
      toast.error('Customer is required');
      return;
    }
    if ((form.order_type === 'lease' || form.order_type === 'rental') && !form.effective_date) {
      toast.error('Effective date is required for lease/rental orders');
      return;
    }
    if (form.order_type === 'rental' && !form.end_date) {
      toast.error('End date is required for rental orders');
      return;
    }
    if (!isEdit && (form.order_type === 'lease' || form.order_type === 'rental') && newLines.length === 0) {
      toast.error('At least one equipment line is required');
      return;
    }
    if (lineForm.item_type || lineForm.quantity) {
      toast.error('Did you forget to confirm the last line item?');
      return;
    }
    setSaving(true);
    try {
      const body: any = {
        customer_org_id: form.customer_org_id || null,
        customer_name: form.customer_name || null,
        order_type: form.order_type,
        delivery_date: form.delivery_date || null,
        effective_date: form.effective_date || null,
        end_date: form.end_date || null,
        notes: form.notes || null,
      };
      if (isNewCustomer && !form.customer_org_id) {
        body.new_customer = {
          contact_name: newCustomer.contact_name || null,
          contact_email: newCustomer.contact_email || null,
          contact_phone: newCustomer.contact_phone || null,
          notes: newCustomer.notes || null,
        };
      }
      if (isEdit) {
        body.status = form.status;
        body.ship_date = form.ship_date || null;
        await api.patch(`/orders/${id}`, { body });
        toast.success('Order updated');
        history.push(`/orders/${id}`);
      } else {
        if (newLines.length > 0) body.lines = newLines;
        const created = await api.post('/orders', { body });
        toast.success('Order created');
        history.push(`/orders/${created.id}`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Line add (edit mode — saved to DB) ----------
  const handleAddLine = async () => {
    if (!lineForm.item_type) { toast.error('Select an item type'); return; }
    if (!lineForm.quantity || parseInt(lineForm.quantity) <= 0) { toast.error('Quantity must be greater than 0'); return; }
    try {
      const body = {
        item_type: lineForm.item_type,
        quantity: parseInt(lineForm.quantity),
        rental_duration_days: lineForm.rental_duration_days ? parseInt(lineForm.rental_duration_days) : null,
        notes: lineForm.notes || null,
        ...lineProductBody(),
        ...(mayPrice ? { unit_price_text: lineForm.unit_price_text || null } : {}),
      };
      const newLine = await api.post(`/orders/${id}/lines`, { body });
      setLines(prev => [...prev, newLine]);
      setLineForm({ item_type: '', quantity: '', rental_duration_days: '', notes: '', unit_price_text: '', part_catalog_id: '', variant: '' });
      toast.success('Line added');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to add line');
    }
  };

  // ---------- Line delete (edit mode) ----------
  const handleDeleteLine = async (lineId: string) => {
    if (!window.confirm('Delete this line?')) return;
    try {
      await api.delete(`/orders/lines/${lineId}`);
      setLines(prev => prev.filter((l: any) => l.id !== lineId));
      toast.success('Line removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ---------- Line add (create mode — local state) ----------
  const handleAddNewLine = () => {
    if (!lineForm.item_type) { toast.error('Select an item type'); return; }
    if (!lineForm.quantity || parseInt(lineForm.quantity) <= 0) { toast.error('Quantity must be greater than 0'); return; }
    setNewLines(prev => [...prev, {
      item_type: lineForm.item_type,
      quantity: parseInt(lineForm.quantity),
      ...lineProductBody(),
      ...(mayPrice ? { unit_price_text: lineForm.unit_price_text || null } : {}),
    }]);
    setLineForm(f => ({ ...f, item_type: '', quantity: '', unit_price_text: '', part_catalog_id: '', variant: '' }));
  };

  if (loading) return <LogoSpinner label="Loading the order" />;

  const isLeaseOrRental = form.order_type === 'rental' || form.order_type === 'lease';
  const statusList = isLeaseOrRental ? LEASE_RENTAL_STATUSES : SALE_STATUSES;

  // Style tokens
  const labelClass = 'block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5';

  const newTotal = orderTotal(newLines);
  const showNewTotals = mayPrice && newLines.length > 0 && (newTotal.total > 0 || newTotal.complete);
  const newCaveat = totalCaveat(newTotal);
  const savedTotal = orderTotal(lines);
  const showSavedTotals = mayPrice && lines.length > 0 && (savedTotal.total > 0 || savedTotal.complete);
  const savedCaveat = totalCaveat(savedTotal);
  const lineMeta: LineMeta = {
    productLabel,
    onRemove: (index) => setNewLines(prev => prev.filter((_, idx) => idx !== index)),
    onDelete: handleDeleteLine,
  };

  const typeLabel = form.order_type === 'lease' ? 'Lease' : form.order_type === 'rental' ? 'Rental' : 'Sales';

  return (
    <PageLayout kind="workspace" className="pt-6 space-y-5">

      <BackLink to={`/orders#${form.order_type}`} label="Orders" />

      <h1 className="text-2xl font-bold text-primary">
        {isEdit ? `Edit ${order?.so_number || ''}` : `New ${typeLabel} Order`}
      </h1>

      {/* ─── Main Form ─── */}
      <form onSubmit={handleSubmit} className="max-w-5xl space-y-5">
          {/* ── Order Information ── */}
          <Panel title="Order Information" titleStyle="section" accent="primary" lit>
          <div className="space-y-5">

          <div>
            <label className={labelClass}>Customer</label>
            <CustomerPicker
              customers={customers}
              selectedId={form.customer_org_id}
              freetext={form.customer_name}
              onSelect={(id, name) => { set('customer_org_id', id || ''); set('customer_name', name || ''); }}
              onNewCustomer={(isNew) => {
                setIsNewCustomer(isNew);
                if (!isNew) setNewCustomer({ contact_name: '', contact_email: '', contact_phone: '', notes: '' });
              }}
            />
            <Reveal show={!!isNewCustomer}>
              <Panel on="panel" level={3} padding="sm" title="New Customer Details" titleStyle="section" className="mt-3">
                <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <FieldRow label="Contact Name">
                    <Field type="text" value={newCustomer.contact_name} onChange={e => setNewCustomer(p => ({ ...p, contact_name: e.target.value }))}
                      placeholder="e.g. John Smith" />
                  </FieldRow>
                  <FieldRow label="Email">
                    <Field type="email" value={newCustomer.contact_email} onChange={e => setNewCustomer(p => ({ ...p, contact_email: e.target.value }))}
                      placeholder="john@company.com" />
                  </FieldRow>
                  <FieldRow label="Phone">
                    <Field type="tel" value={newCustomer.contact_phone} onChange={e => setNewCustomer(p => ({ ...p, contact_phone: e.target.value }))}
                      placeholder="(555) 123-4567" />
                  </FieldRow>
                </div>
                <FieldRow label="Notes">
                  <Field type="text" value={newCustomer.notes} onChange={e => setNewCustomer(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Optional notes about this customer" />
                </FieldRow>
                </div>
              </Panel>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <FieldRow label="Order Type">
              <Select
                label="Order type"
                value={form.order_type}
                onChange={key => set('order_type', key)}
                disabled={isEdit || !!searchParams.get('type')}
                options={[{ key: 'sale', label: 'Sale' }, { key: 'lease', label: 'Lease' }, { key: 'rental', label: 'Rental' }]}
              />
            </FieldRow>

            {form.order_type === 'sale' && (
              <FieldRow label="Delivery Date">
                <Field type="date" value={form.delivery_date} onChange={e => set('delivery_date', e.target.value)} />
              </FieldRow>
            )}

            {isLeaseOrRental && (
              <>
                <FieldRow label="Effective Date *">
                  <Field type="date" value={form.effective_date} onChange={e => set('effective_date', e.target.value)} required />
                </FieldRow>
                <FieldRow label={<>End Date {form.order_type === 'rental' ? '*' : <span className="text-on-surface-variant">(optional)</span>}</>}>
                  <Field type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} required={form.order_type === 'rental'} />
                </FieldRow>
              </>
            )}

            {isEdit && (
              <FieldRow label="Status">
                <Select
                  label="Status"
                  value={form.status}
                  onChange={key => set('status', key)}
                  options={statusList.map(st => {
                    const words = st.replace(/_/g, ' ');
                    return { key: st, label: words.charAt(0).toUpperCase() + words.slice(1) };
                  })}
                />
              </FieldRow>
            )}

            {isEdit && (
              <FieldRow label="Ship Date">
                <Field type="date" value={form.ship_date} onChange={e => set('ship_date', e.target.value)} />
              </FieldRow>
            )}
          </div>
          </div>
          </Panel>

          {/* ── Equipment Lines (Create mode) ── */}
          {!isEdit && (
            <Panel title="Equipment" titleStyle="section" accent="blue">
            <div className="space-y-4">
              {newLines.length === 0 ? (
                <EmptyState variant="area">No equipment added yet. Use the form below to add line items.</EmptyState>
              ) : (
                <DataTable<NewLineRow>
                  data={newLines}
                  columns={showNewTotals ? NEW_COLUMNS_TOTALED : NEW_COLUMNS}
                  getRowId={(_, i) => String(i)}
                  columnVisibility={NEW_VIS[String(mayPrice) as 'true' | 'false']}
                  meta={lineMeta}
                  on="panel"
                  empty={null}
                  emptyMode="replace"
                  footer={showNewTotals && newCaveat ? <span className="ml-auto text-xs text-on-surface-variant">{newCaveat}</span> : undefined}
                />
              )}

              {/* Add line mini-form */}
              <div className={`grid ${mayPrice ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'} gap-3 items-end`}>
                <ItemChoiceSelect choices={choices} itemType={lineForm.item_type} productId={lineForm.part_catalog_id}
                  onChange={setLineChoice} />
                {showProductFields && (
                  <LineProductFields products={lineProducts} productId={lineForm.part_catalog_id} variant={lineForm.variant} itemTypeFixesVariant={variantOfItemType(lineForm.item_type) !== null}
                    showProduct={lineProduct === null} onChange={setLineProduct} />
                )}
                <FieldRow label="Quantity">
                  <Field type="number" min="1" value={lineForm.quantity}
                    onChange={e => setLineForm({ ...lineForm, quantity: e.target.value })} />
                </FieldRow>
                {mayPrice && (
                  <FieldRow label="Price">
                    <Field type="text" value={lineForm.unit_price_text}
                      onChange={e => setLineForm({ ...lineForm, unit_price_text: e.target.value })}
                      placeholder={pricePlaceholder(ITEM_TYPES.find(t => t.value === lineForm.item_type))} />
                  </FieldRow>
                )}
                <div>
                  <button type="button" onClick={handleAddNewLine}
                    className={`${buttonClass('quiet', 'md')} w-full`}>
                    + Add
                  </button>
                </div>
              </div>
            </div>
            </Panel>
          )}

          {/* ── Equipment Lines (Edit mode) ── */}
          {isEdit && (
            <Panel title="Line Items" titleStyle="section" accent="blue">
            <div className="space-y-4">
              {lines.length > 0 && (
                <DataTable<SavedLineRow>
                  data={lines}
                  columns={showSavedTotals ? SAVED_COLUMNS_TOTALED : SAVED_COLUMNS}
                  getRowId={(l) => String(l.id)}
                  columnVisibility={SAVED_VIS[`${mayPrice}-${isLeaseOrRental}` as keyof typeof SAVED_VIS]}
                  meta={lineMeta}
                  on="panel"
                  empty={null}
                  emptyMode="replace"
                  footer={showSavedTotals && savedCaveat ? <span className="ml-auto text-xs text-on-surface-variant">{savedCaveat}</span> : undefined}
                />
              )}

              {/* Add line form */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                <ItemChoiceSelect choices={choices} itemType={lineForm.item_type} productId={lineForm.part_catalog_id}
                  onChange={setLineChoice} />
                {showProductFields && (
                  <LineProductFields products={lineProducts} productId={lineForm.part_catalog_id} variant={lineForm.variant} itemTypeFixesVariant={variantOfItemType(lineForm.item_type) !== null}
                    showProduct={lineProduct === null} onChange={setLineProduct} />
                )}
                <FieldRow label="Quantity">
                  <Field type="number" min="1" value={lineForm.quantity}
                    onChange={e => setLineForm({ ...lineForm, quantity: e.target.value })} />
                </FieldRow>
                {isLeaseOrRental && (
                  <FieldRow label="Rental Days">
                    <Field type="number" min="1" value={lineForm.rental_duration_days}
                      onChange={e => setLineForm({ ...lineForm, rental_duration_days: e.target.value })} />
                  </FieldRow>
                )}
                {mayPrice && (
                  <FieldRow label="Price">
                    <Field type="text" value={lineForm.unit_price_text}
                      onChange={e => setLineForm({ ...lineForm, unit_price_text: e.target.value })}
                      placeholder={pricePlaceholder(ITEM_TYPES.find(t => t.value === lineForm.item_type))} />
                  </FieldRow>
                )}
                <FieldRow label="Notes">
                  <Field type="text" value={lineForm.notes}
                    onChange={e => setLineForm({ ...lineForm, notes: e.target.value })} />
                </FieldRow>
              </div>
              <button type="button" onClick={handleAddLine}
                className={buttonClass('quiet', 'md')}>
                + Add Line
              </button>
            </div>
            </Panel>
          )}

          {/* ── Notes ── */}
          <Panel label="Notes">
            <FieldRow label="Notes">
              <TextArea
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                rows={3}
                placeholder="Internal notes for this order..."
              />
            </FieldRow>
          </Panel>

          {/* ── Submit ── */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className={buttonClass('filled', 'md')}
            >
              {saving
                ? (isEdit ? 'Saving...' : 'Creating...')
                : (isEdit ? 'Save Changes' : 'Create Order')}
            </button>
            <Link to={`/orders#${form.order_type}`} className={buttonClass('quiet', 'md')}>
              Cancel
            </Link>
          </div>
      </form>

      {/* ─── Sellable Availability (sales) ─── */}
      {!isEdit && form.order_type === 'sale' && availability && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Sellable Fleet */}
          <Panel title="Sellable Fleet" level={3} titleStyle="section" accent="blue" lit>
            <div className="flex items-baseline gap-4">
              <div className="text-3xl font-bold text-blue-400 tracking-tight tabular-nums">
                {availability.sellableFleet ?? '\u2014'}
              </div>
              {availability.sellableFleet != null && (
                <div className="flex items-baseline gap-3 text-sm text-on-surface-variant">
                  <span>{availability.sellableLs} <span className="text-[10px] text-on-surface-variant">std</span></span>
                  <span>{availability.sellablePyro} <span className="text-[10px] text-on-surface-variant">pyro</span></span>
                </div>
              )}
            </div>
            {availability.sellableFleet != null && availability.committedTotal > 0 && (
              <div className="flex items-baseline gap-2 text-sm mt-1.5">
                <span className="text-orange-400/80">-{availability.committedTotal} committed</span>
                <span className="text-on-surface-disabled">=</span>
                <span className={`font-bold ${(availability.sellableFleet - availability.committedTotal) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {availability.sellableFleet - availability.committedTotal} available
                </span>
              </div>
            )}
            <div className="text-[11px] text-on-surface-variant mt-1.5">
              {availability.sellableFleet != null ? 'Available for sales' : 'Not set yet'}
            </div>
          </Panel>

          {/* Sellable Batteries */}
          <Panel title="Sellable Batteries" level={3} titleStyle="section" accent="green" lit>
            <div className="text-3xl font-bold text-green-400 tracking-tight tabular-nums">
              {availability.sellableBatteries ?? '\u2014'}
            </div>
            {availability.sellableBatteries != null && availability.committedBatteries > 0 && (
              <div className="flex items-baseline gap-2 text-sm mt-1.5">
                <span className="text-orange-400/80">-{availability.committedBatteries} committed</span>
                <span className="text-on-surface-disabled">=</span>
                <span className={`font-bold ${(availability.sellableBatteries - availability.committedBatteries) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {availability.sellableBatteries - availability.committedBatteries} available
                </span>
              </div>
            )}
            <div className="text-[11px] text-on-surface-variant mt-1.5">
              {availability.sellableBatteries != null ? 'Battery inventory for sales' : 'Not set yet'}
            </div>
          </Panel>
        </div>
      )}

      {/* ─── Forecast Chart (lease/rental) ─── */}
      <Reveal show={!isEdit && !!isLeaseOrRental}>
        <Panel label="Forecast">
          <ForecastChart />
        </Panel>
      </Reveal>
    </PageLayout>
  );
};

export default SalesOrderForm;
